import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion, AnimatePresence } from 'framer-motion';
import { Quote, ArrowRight, X } from 'lucide-react';
import { Link } from 'react-router-dom';

const Grooming = () => {
  const [selectedImage, setSelectedImage] = useState(null);

  const galleryImages = [
    "https://assets.auxodev.dk/assets/97a23b31-82db-40a5-a298-876516351407/50921792-1ea0-4f00-8a65-a455cbacf0ee.jpeg",
    "https://assets.auxodev.dk/assets/97a23b31-82db-40a5-a298-876516351407/7f8eb256-5e34-49fb-aba7-52ba08b71f89.jpeg",
    "https://assets.auxodev.dk/assets/97a23b31-82db-40a5-a298-876516351407/88659815-18f1-4a8f-8572-06540835d88a.jpeg",
    "https://assets.auxodev.dk/assets/97a23b31-82db-40a5-a298-876516351407/0cef5ca6-cc47-4877-9366-acc0ea8ec8fa.jpg"
  ];

  return (
    <div className="bg-white">
      <Helmet>
        <title>Klipning — Toftes Hundesalon</title>
        <meta name="description" content="Professionel hundeklipning og grooming i Tranbjerg. Vi håndterer alle hunderacer og temperamenter med tålmodighed og kærlighed." />
        <meta property="og:title" content="Klipning — Toftes Hundesalon" />
      </Helmet>

      {/* Header Section */}
      <section className="py-24 px-4 bg-stone-50">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-sm uppercase tracking-[0.4em] font-bold text-stone-700 mb-6">Pleje & Grooming</h2>
          <h1 className="!text-[2rem] md:text-6xl font-serif italic text-stone-900 mb-10 leading-tight">
            Din hund er <br /> i fokus
          </h1>
          <div className="prose prose-lg mx-auto text-gray-600 italic mb-12 leading-relaxed">
            <p>
              “Mange tror fejlagtigt, at hundeklipning er forbeholdt de langhårede racer. Korthårede hunderacer som fx. Schäfer, har det med at fælde meget, og vil derfor også nyde godt af en soignering hos en professionel hundefrisør.”
            </p>
            <p>
              Hos Toftes Hundesalon bliver din hund forkælet - din hund bliver vasket, friséret og plejet fra top til tå.
            </p>
          </div>
          <a 
            href="https://tofteshundesalon.planway.com/" 
            target="_blank" 
            rel="noopener noreferrer"
            className="bg-stone-800 text-white px-12 py-4 rounded-none text-xs uppercase tracking-widest font-bold hover:bg-stone-900 transition-all inline-block"
          >
            Book din næste tid
          </a>
        </div>
      </section>

      {/* Problemhund Section */}
      <section className="py-24 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-20 items-center">
            <div>
              <h2 className="text-sm uppercase tracking-[0.4em] font-bold text-stone-700 mb-6">Specialisering</h2>
              <h3 className="!text-[1.6rem] md:text-6xl font-serif italic text-stone-900 mb-8 leading-tight">Problemhund?</h3>
              <div className="prose prose-lg text-gray-600 leading-relaxed">
                <p className="mb-6">
                  Er din pelsklædte ven lidt af en udfordring på hundefrisør-fronten? Frygt ej! Jeg er her for at tilbyde min ekspertise i håndtering af alle slags hunde, uanset temperament.
                </p>
                <p className="mb-6">
                  Som erfaren hundefrisør har jeg også specialiseret mig i at arbejde med problemhunde og dem, der har lidt ekstra ”bid” i sig.
                </p>
                <p className="mb-6">
                  Hos Toftes Hundesalon tror jeg på, at hver hund er unik, og det indebærer at forstå deres individuelle behov og personligheder. Jeg har dybdegående viden om hundens adfærd og genetik, hvilket gør mig i stand til at læse deres signaler og reagere på dem på en måde, der fremmer tillid og afslapning.
                </p>
                <p className="mb-6">
                  So hvis du har en hund, der har brug for lidt ekstra kærlig pleje eller er en bisse, der skal forkæles med en ny frisure, er I også velkommen her.
                </p>
                <p className="italic text-stone-800 font-serif text-xl pt-4">
                  – Helene
                </p>
              </div>
            </div>
            <div className="relative">
              <div className="absolute -bottom-10 -right-10 w-40 h-40 bg-stone-100 -z-10" />
              <img 
                src="https://assets.auxodev.dk/assets/97a23b31-82db-40a5-a298-876516351407/5544c3cc-aabc-47bc-98f4-2156f941d72e.jpg" 
                alt="Problemhund håndtering" 
                className="w-full rounded-none shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section className="py-24 px-4 bg-stone-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {galleryImages.map((img, idx) => (
              <motion.div 
                key={idx} 
                className="aspect-square overflow-hidden shadow-sm cursor-pointer group"
                whileHover={{ scale: 1.02 }}
                onClick={() => setSelectedImage(img)}
              >
                <img 
                  src={img} 
                  alt={`Grooming ${idx}`} 
                  className="w-full h-full object-cover transition-all duration-500" 
                />
              </motion.div>
            ))}
          </div>
          <div className="text-center mt-16">
            <Link to="/galleri" className="text-xs uppercase tracking-[0.4em] font-bold text-stone-800 hover:text-stone-600 inline-flex items-center gap-3">
              Se flere billeder her <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Lightbox */}
      <AnimatePresence>
        {selectedImage && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedImage(null)}
            className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4 md:p-10 cursor-zoom-out"
          >
            <button 
              className="absolute top-6 right-6 text-white hover:text-amber-300 transition-colors"
              onClick={() => setSelectedImage(null)}
            >
              <X className="w-8 h-8" />
            </button>
            <motion.img 
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              src={selectedImage} 
              alt="Full size" 
              className="max-w-full max-h-full object-contain shadow-2xl"
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Quote Section */}
      <section className="py-32 px-4 bg-stone-100 text-stone-900 text-center relative overflow-hidden border-t border-stone-200">
        <div className="absolute top-0 right-0 w-1/3 h-full bg-stone-200/50 -skew-x-12 translate-x-1/2" />
        <div className="max-w-4xl mx-auto relative z-10">
          <Quote className="w-12 h-12 mx-auto mb-10 text-stone-400 opacity-50" />
          <h2 className="text-4xl md:text-5xl font-serif italic mb-12 leading-tight text-stone-900">
            “En hund er ligeglad med hvordan du ser ud, om du er rig eller fattig, om du er tyk eller tynd. Hvis du skænker den dit hjerte, så gør den det ligeså.”
          </h2>
          <p className="text-xs uppercase tracking-[0.4em] font-bold text-stone-600">— Helene, Toftes Hundesalon</p>
          <div className="mt-16">
            <a 
              href="https://tofteshundesalon.planway.com/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="bg-stone-800 text-white px-12 py-5 rounded-none text-xs uppercase tracking-widest font-bold hover:bg-stone-900 transition-all inline-block"
            >
              Bestil en tid
            </a>
          </div>
        </div>
      </section>

    </div>
  );
};

export default Grooming;
